import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '';

const api = axios.create({
  baseURL: `${API_URL}/api`,
  timeout: 30000, // 30 seconds - scraping can take time
  headers: {
    'Content-Type': 'application/json'
  }
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const isAuthEndpoint = error.config?.url?.includes('/auth/login') ||
                           error.config?.url?.includes('/auth/register');
    if (error.response?.status === 401 && !isAuthEndpoint) {
      localStorage.removeItem('token');
      localStorage.setItem('session_expired', '1');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const checkPricesNow = () => api.post('/products/check-prices');

export const getNotificationHistory = (page = 1, limit = 20) => {
  return api.get(`/notifications?page=${page}&limit=${limit}`);
};

export const getNotificationCountSince = (since) => {
  return api.get(`/notifications/count?since=${encodeURIComponent(since)}`);
};

export const getNotificationScreenshot = (id) => api.get(`/notifications/${id}/screenshot`);

export const forgotPassword = (email) => api.post('/auth/forgot-password', { email });
export const resetPassword = (token, password) => api.post('/auth/reset-password', { token, password });
export const resendVerification = () => api.post('/auth/resend-verification');

export const previewProduct = (url) => api.post('/auth/preview-product', { url });
export const registerAndTrack = (payload) => api.post('/auth/register-and-track', payload);

export const getFeaturedDeals = (src) => api.get('/featured-deals', { params: src ? { src } : undefined });
export const getPublicStats = () => api.get('/stats/public');
export const updateCadence = (cadence) => api.put('/user/profile/cadence', { cadence });
// §1.1 premium cheaper-elsewhere alerts — per-feature switch, settable by free
// users too (the preference survives an upgrade; the gate is at send time).
export const setCrossRetailerAlerts = (enabled) =>
  api.put('/user/profile/cross-retailer-alerts', { enabled });
export const setPassword = (token, password) => api.post('/auth/reset-password', { token, password });

// Telegram alert channel
export const getTelegramStatus = () => api.get('/telegram/status');
export const createTelegramLink = () => api.post('/telegram/link');
export const disconnectTelegram = () => api.post('/telegram/disconnect');
export const toggleTelegram = (enabled) => api.put('/telegram/toggle', { enabled });

// Web Push channel. getPushConfig is what decides whether the card renders at
// all — keys unset on the server means the whole channel is a no-op.
export const getPushConfig = () => api.get('/push/public-key');
export const savePushSubscription = (sub) => api.post('/push/subscribe', sub);
export const deletePushSubscription = (endpoint) => api.post('/push/unsubscribe', { endpoint });
export const listPushSubscriptions = () => api.get('/push/subscriptions');

export const getProfile = () => api.get('/user/profile');
export const updateName = (name) => api.put('/user/profile', { name });
export const updateEmail = (email, currentPassword) => api.put('/user/profile/email', { email, currentPassword });
export const updatePassword = (currentPassword, newPassword) => api.put('/user/profile/password', { currentPassword, newPassword });
export const deleteAccount = (password) => api.delete('/user/profile', { data: { password } });

export const admin = {
  getStats: () => api.get('/admin/stats'),
  getUsers: (params) => api.get('/admin/users', { params }),
  getUserFilterOptions: () => api.get('/admin/users/filter-options'),
  getUser: (id) => api.get(`/admin/users/${id}`),
  updateUser: (id, data) => api.put(`/admin/users/${id}`, data),
  resetPassword: (id, data) => api.put(`/admin/users/${id}/password`, data),
  setUserPremium: (id, premium) => api.patch(`/admin/users/${id}/premium`, { premium }),
  deleteUser: (id) => api.delete(`/admin/users/${id}`),
  getProducts: (search) => api.get('/admin/products', { params: { search } }),
  updateProduct: (id, data) => api.put(`/admin/products/${id}`, data),
  deleteProduct: (id) => api.delete(`/admin/products/${id}`),
  getScraperHealth: () => api.get('/admin/scraper-health'),
  checkPrices: () => api.post('/admin/check-prices'),
  refreshFeaturedDeals: () => api.post('/admin/featured-deals/refresh'),
  getCommunications: (params) => api.get('/admin/communications', { params }),
  getCommunication: (id) => api.get(`/admin/communications/${id}`),
  getSettings: () => api.get('/admin/settings'),
  updateSetting: (key, value) => api.put('/admin/settings', { key, value }),
  listCampaigns: () => api.get('/admin/campaigns'),
  getCampaign: (id) => api.get(`/admin/campaigns/${id}`),
  createCampaign: (data) => api.post('/admin/campaigns', data),
  updateCampaign: (id, data) => api.put(`/admin/campaigns/${id}`, data),
  deleteCampaign: (id) => api.delete(`/admin/campaigns/${id}`),
  sendCampaignNow: (id) => api.post(`/admin/campaigns/${id}/send`),
  testCampaign: (id, email) => api.post(`/admin/campaigns/${id}/test`, { email }),
  audiencePreview: (filters) => api.post('/admin/campaigns/audience-preview', { filters: filters || {} }),
  renderCampaignPreview: (html, useBrandShell) => api.post('/admin/campaigns/render-preview', { html, use_brand_shell: !!useBrandShell }),
  getClickStats: (days) => api.get('/admin/clicks', { params: { days } }),
};

export const submitFeedback = ({ type, message, rating, screenshot, includeActivity }) => {
  if (screenshot) {
    const fd = new FormData();
    fd.append('type', type);
    fd.append('message', message);
    if (rating != null) fd.append('rating', String(rating));
    fd.append('includeActivity', String(includeActivity ?? true));
    fd.append('screenshot', screenshot, screenshot.name);
    // Override the instance's default application/json content-type. With JSON
    // set, axios serialises FormData via formDataToJSON() and silently drops the
    // File. Setting multipart/form-data lets the browser add the boundary so the
    // file actually uploads (multer then populates req.file).
    return api.post('/feedback', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  }
  return api.post('/feedback', { type, message, rating, includeActivity: includeActivity ?? true });
};
export const getMyFeedback = () => api.get('/feedback');
export const searchAmazonProducts = (q, domain) => api.get('/products/amazon-search', { params: { q, domain } });

export default api;
